import React from 'react'
import { useTranslation } from 'react-i18next'
import { useMediaQuery } from 'react-responsive'
import { useHistory } from 'react-router'
import { profileStore, projectStore } from '~/stores'
import { observer } from '~/ui/component'
import {
  Center,
  EmptyOrFetching,
  HBox,
  Hero,
  PushButton,
  Scroller,
  TextBlock,
  TextField,
  VBox,
} from '~/ui/components'
import { AppLayoutConfig } from '~/ui/layouts'
import { createUseStyles, layout, useStyling } from '~/ui/styling'
import { screenMinWidths } from '~/ui/styling/layout'
import { renderHomeScreenWidget } from './widgets'
import { ImageView } from '../media'

export interface Props {

}

const HomeScreen = observer('HomeScreen', (props: Props) => {

  const [t] = useTranslation('home')

  const config      = projectStore.project?.homeScreen
  const participant = profileStore.participant

  const history = useHistory()

  const [query, setQuery] = React.useState<string | null>(null)

  const search = React.useCallback(() => {
    if (query == null) { return null }

    history.push(`/search?query=${encodeURIComponent(query)}`)
  }, [history, query])

  const isMobile = !useMediaQuery({minWidth: screenMinWidths.tablet})

  const {guide} = useStyling()

  const footerLogo = guide.images['logo:footer']

  //------
  // Rendering

  const $ = useStyles()

  function render() {
    return (
      <>
        <AppLayoutConfig
          title={t('title')}
        />
        {config == null ? renderEmpty() : (
          <Scroller flex contentClassNames={$.HomeScreen}>
            <VBox>
              {renderHero()}
              {renderWidgets()}
              {renderFooter()}
            </VBox>
          </Scroller>
        )}
      </>
    )
  }

  function renderHero() {
    if (config == null) { return null }
    if (config.hero.background != null && config.hero.background.type === 'image') {
      return (
        <Hero
          backgroundImage={config.hero.background.image}
          darkenBackground={config.hero.background.darken}
          fadeBackground={config.hero.background.fade}
        >
          {renderHeroContent()}
        </Hero>
      )
    } else {
      return (
        <VBox classNames={$.bareHero}>
          {renderHeroContent()}
        </VBox>
      )
    }
  }

  function renderHeroContent() {
    if (config == null || (!config.hero.search && !config.hero.welcomeMessage)) { return null }

    return (
      <VBox>
        {renderMessage()}
        {renderSearch()}
      </VBox>
    )
  }

  function renderEmpty() {
    return (
      <EmptyOrFetching
        status='fetching'
      />
    )
  }

  function renderMessage() {
    if (!config?.hero.welcomeMessage) { return null }

    return (
      <TextBlock classNames={$.message} markup>
        {config.hero.welcomeMessage ?? t('welcome', participant)}
      </TextBlock>
    )
  }

  function renderSearch() {
    if (!config?.hero.search) { return null }

    return (
      <form onSubmit={search}>
        <HBox classNames={$.searchFieldWrap} gap={layout.padding.inline.m}>
          <TextField
            classNames={$.searchField}
            value={query ?? ''}
            onChange={setQuery}
            placeholder={t('search.placeholder')}
          />
          <PushButton
            aria-label={t('search.button')}
            caption={isMobile ? undefined : t('search.button')}
            icon='search'
            submit
          />
        </HBox>

      </form>
    )
  }

  function renderWidgets() {
    return (
      <VBox classNames={$.widgets} gap={layout.padding.xl}>
        {config?.widgets.map((widget, index) => (
          <VBox key={`${widget.type}_${index}`}>
            {renderHomeScreenWidget(widget)}
          </VBox>
        ))}
      </VBox>
    )
  }

  function renderFooter() {
    if (footerLogo == null) { return null }
    return (
      <Center>
        <ImageView
          source={footerLogo}
          size={footerLogoSize}
          objectFit='contain'
        />
      </Center>
    )
  }

  return render()

})

export default HomeScreen

export const footerLogoSize = {
  width:  150,
  height: 100,
}

const useStyles = createUseStyles(theme => ({
  HomeScreen: {
  },

  bareHero: {
    ...layout.responsive(size => ({
      padding: [layout.padding.l[size], layout.padding.m[size]],
      paddingBottom: 0,
    })),
  },

  widgets: {
    ...layout.responsive(size => ({
      padding: [layout.padding.xl[size], layout.padding.m[size]],
    })),
    maxWidth: 1200,
  },

  message: {
    fontSize: 26,
    fontWeight: 500,
    lineHeight: '130%',
    paddingBottom: layout.padding.l.mobile,
    ...layout.tablet({
      fontSize: 30,
      paddingBottom: layout.padding.l.tablet,
    }),
    ...layout.desktop({
      fontSize: 30,
      paddingBottom: layout.padding.l.desktop,
    }),
  },

  searchFieldWrap: {
    maxWidth: 600,
  },

  searchField: {
    flexGrow: 1,
  },
}))